import { useEffect, useState } from "react";
import styled from "styled-components";
import axios from "axios";
import Loader from "../components/Loader";
import { useNavigate } from "react-router-dom";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Page = styled.div`
  min-height: 100vh;
  background: #f6f8fb;
  font-family: 'Segoe UI', 'Inter', Arial, sans-serif;
`;

const Container = styled.div`
  max-width: 1240px;
  margin: 0 auto;
  padding: 24px 18px 32px;
`;

const PageTitle = styled.h2`
  font-size: 1.6rem;
  font-weight: 700;
  color: #21325b;
  margin-bottom: 18px;
  flex: 6;
`;

const TableCard = styled.div`
  background: #fff;
  border-radius: 14px;
  box-shadow: 0 2px 10px rgba(35,62,88,0.06);
  padding: 20px;
  margin-bottom: 32px;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  th, td {
    text-align: left;
    padding: 12px 16px;
    border-bottom: 1px solid #eee;
  }
  th {
    font-weight: 600;
    color: #2b3b5f;
  }
`;

const Button = styled.button`
  background: #3772FF;
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 0.5rem 2rem;
  min-width: 140px;
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  box-shadow: 0 1px 2px rgba(35,62,88,0.05);
  &:hover {
    background: #245bb9;
  }
  margin-top: 10px;
`;

const ToggleButton = styled.button`
  background: ${props => props.active ? '#3772FF' : '#ddd'};
  color: ${props => props.active ? '#fff' : '#333'};
  border: none;
  border-radius: 6px;
  padding: 6px 14px;
  cursor: pointer;
  font-weight: 600;
  font-size: 0.9rem;
  &:hover {
    background: ${props => props.active ? '#245bb9' : '#bbb'};
  }
`;
const ButtonCancel = styled.button`
  border: none;
  border-radius: 6px;
  padding: 6px 14px;
  cursor: pointer;
  font-weight: 600;
  font-size: 0.9rem;
  color: #fff;
  background: ${props => props.variant === 'cancel' ? '#d24949' : '#426dff'};
  &:hover {
    background: ${props => props.variant === 'cancel' ? '#b33b3b' : '#305de2'};
  }
`;

export default function SettingsPage() {
  const userData = {
    user: JSON.parse(localStorage.getItem('user')),
    token: localStorage.getItem('access_token'),
  };
  const isAdmin = userData.user.roles.role_name === "SUPER_ADMIN";
  const headers = { Authorization: `Bearer ${userData.token}` };

  // Email Recipients State
  const [emailRecipients, setEmailRecipients] = useState([]);
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailError, setEmailError] = useState('');

  // Currency Denominations State
  const [currencyDenoms, setCurrencyDenoms] = useState([]);
  const [denomLoading, setDenomLoading] = useState(false);
  const [denomError, setDenomError] = useState('');

  // Digital Settlements State
  const [digitalSettlements, setDigitalSettlements] = useState([]);
  const [digitalLoading, setDigitalLoading] = useState(false);
  const [digitalError, setDigitalError] = useState('');

  // Editable fields for adding or editing
  const [newEmail, setNewEmail] = useState('');
  const [newDenomValue, setNewDenomValue] = useState('');
  const [newDigitalName, setNewDigitalName] = useState('');
  // Mail Triggers State
const [mailTriggers, setMailTriggers] = useState([]);
const [mailLoading, setMailLoading] = useState(false);
const [mailError, setMailError] = useState('');
const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

const validateName = (name) => /^[a-zA-Z0-9\s'\-\+\(\)]{3,50}$/.test(name);
const validateDenomination = (value) => /^[1-9]\d*$/.test(value); // positive integers only

  const navigate = useNavigate()
const hostname = window.location.hostname;
  const isInventoryDomain = hostname === 'inventory.fescafe.in' || hostname === 'localhost';
  const isRecordsDomain = hostname === 'records.fescafe.in' ;
useEffect(() => {
    if(!isAdmin)
      navigate(isRecordsDomain ?'/cashflow' : '/inventory/entries')
  }, []);
// Fetch Mail Triggers
const fetchMailTriggers = async () => {
  setMailLoading(true);
  setMailError('');
  try {
    const res = await axios.get(`${BACKEND_URL}/settings/mail-trigger`, { headers });
    setMailTriggers(res.data);
  } catch {
    setMailError('Failed to fetch mail triggers');
  } finally {
    setMailLoading(false);
  }
};

// Toggle a trigger
const toggleMailTrigger = async (module_name, trigger_name, currentValue) => {
  if (!isAdmin) return;
  try {
    await axios.post(
      `${BACKEND_URL}/settings/mail-trigger/${module_name}`,
      { trigger_name, trigger_value: !currentValue },
      { headers }
    );
    fetchMailTriggers();
  } catch {
    setMailError('Failed to update mail trigger');
  }
};


  // Toggle Active Status Functions
  const toggleEmailActive = async (id, currentStatus) => {
    if(!isAdmin) return;
    try {
      await axios.put(`${BACKEND_URL}/settings/email-recipient/${id}`, { is_active: !currentStatus }, { headers });
      fetchEmailRecipients();
    } catch {
      setEmailError('Failed to update status');
    }
  };

  const toggleDenomActive = async (id, currentStatus) => {
    if(!isAdmin) return;
    try {
      await axios.put(`${BACKEND_URL}/settings/currency-denomination/${id}`, { is_active: !currentStatus }, { headers });
      fetchCurrencyDenoms();
    } catch {
      setDenomError('Failed to update status');
    }
  };

  const toggleDigitalActive = async (id, currentStatus) => {
    if(!isAdmin) return;
    try {
      await axios.put(`${BACKEND_URL}/settings/digital-settlement/${id}`, { is_active: !currentStatus }, { headers });
      fetchDigitalSettlements();
    } catch {
      setDigitalError('Failed to update status');
    }
  };

  // Delete Functions
  const DeleteDenom = async (id) => {
    if(!isAdmin) return;
    try {
      await axios.get(`${BACKEND_URL}/settings/currency-denomination/delete/${id}`, { headers });
      fetchCurrencyDenoms();
    } catch {
      setDenomError('Failed to delete denomination');
    }
  };

  const DeleteEmailRecipients = async (id) => {
    if(!isAdmin) return;
    try {
      await axios.get(`${BACKEND_URL}/settings/email-recipient/delete/${id}`, { headers });
      fetchEmailRecipients();
    } catch {
      setEmailError('Failed to delete email recipient');
    }
  };

  const DeleteDigitalSettlement = async (id) => {
    if(!isAdmin) return;
    try {
      await axios.get(`${BACKEND_URL}/settings/digital-settlement/delete/${id}`, { headers });
      fetchDigitalSettlements();
    } catch {
      setDigitalError('Failed to delete digital settlement');
    }
  };

  // Fetch Functions
  const fetchEmailRecipients = async () => {
    setEmailLoading(true);
    setEmailError('');
    try {
      const res = await axios.get(`${BACKEND_URL}/settings/email-recipient`, { headers });
      setEmailRecipients(res.data);
    } catch {
      setEmailError('Failed to fetch email recipients');
    } finally {
      setEmailLoading(false);
    }
  };

  const fetchCurrencyDenoms = async () => {
    setDenomLoading(true);
    setDenomError('');
    try {
      const res = await axios.get(`${BACKEND_URL}/settings/currency-denomination`, { headers });
      setCurrencyDenoms(res.data);
    } catch {
      setDenomError('Failed to fetch currency denominations');
    } finally {
      setDenomLoading(false);
    }
  };

  const fetchDigitalSettlements = async () => {
    setDigitalLoading(true);
    setDigitalError('');
    try {
      const res = await axios.get(`${BACKEND_URL}/settings/digital-settlement`, { headers });
      setDigitalSettlements(res.data);
    } catch {
      setDigitalError('Failed to fetch digital settlements');
    } finally {
      setDigitalLoading(false);
    }
  };

  // Add Handlers
  const handleAddEmail = async () => {
    if (!newEmail.trim()) {
      setEmailError('Email cannot be empty');
      return;
    }else if(!validateEmail(newEmail)){ setEmailError('Invalid email address'); return}
      else 
        setEmailError('')
    
    try {
      await axios.post(`${BACKEND_URL}/settings/email-recipient`, { email: newEmail.trim(), is_active: true }, { headers });
      setNewEmail('');
      fetchEmailRecipients();
    } catch {
      setEmailError('Failed to add email recipient');
    }
  };

  const handleAddDenom = async () => {
    if (!newDenomValue.trim()) {
      setDenomError('Currency denomination value cannot be empty');
      return;
    }else if(!validateDenomination(newDenomValue)){ setDenomError('Invalid denomination value'); return}
      else 
        setDenomError('')
    
    try {
      await axios.post(`${BACKEND_URL}/settings/currency-denomination`, { currency_denom_value: newDenomValue.trim(), is_active: true }, { headers });
      setNewDenomValue('');
      fetchCurrencyDenoms();
    } catch {
      setDenomError('Failed to add currency denomination');
    }
  };

  const handleAddDigital = async () => {
    if (!newDigitalName.trim()) {
      setDigitalError('Digital settlement name cannot be empty');
      return;
    }else if (!validateName(newDigitalName)) {setDigitalError('3-50 chars; letters, spaces, \'- allowed');return ;}
  else setDigitalError('');
    try {
      await axios.post(`${BACKEND_URL}/settings/digital-settlement`, { digital_settlement_name: newDigitalName.trim(), is_active: true }, { headers });
      setNewDigitalName('');
      fetchDigitalSettlements();
    } catch {
      setDigitalError('Failed to add digital settlement');
    }
  };

  useEffect(() => {
    fetchEmailRecipients();
    fetchCurrencyDenoms();
    fetchDigitalSettlements();
      fetchMailTriggers(); 
  }, []);

  return (
    <Page>
      <Container>
        <PageTitle>Settings Management</PageTitle>

        {/* Email Recipients */}
        <TableCard>
          <h3>Email Recipients</h3>
          {emailLoading && <Loader />}
          <Table>
            <thead>
              <tr>
                <th>ID</th>
                <th>Email</th>
                <th>Is Active</th>
                {isAdmin && <th>Toggle Active</th>}
                {isAdmin && <th>Delete</th>}
              </tr>
            </thead>
            <tbody>
              {emailRecipients.length === 0 ? (
                <tr><td colSpan={5} style={{textAlign: 'center'}}>No email recipients found</td></tr>
              ) : emailRecipients.map(({id, email, is_active}) => (
                <tr key={id}>
                  <td>{id}</td>
                  <td>{email}</td>
                  <td>{is_active ? 'Active' : 'Inactive'}</td>
                  {isAdmin && (
                    <td>
                      <ToggleButton
                        active={is_active}
                        onClick={() => toggleEmailActive(id, is_active)}
                      >
                        {is_active ? 'Deactivate' : 'Activate'}
                      </ToggleButton>
                    </td>
                  )}
                  {isAdmin && (
                    <td><ButtonCancel variant="cancel" onClick={() => DeleteEmailRecipients(id)}>Delete</ButtonCancel></td>
                  )}
                </tr>
              ))}
              {isAdmin && (
                <tr>
                  <td>New</td>
                  <td>
                    <input
                      type="email"
                      placeholder="Email address"
                      value={newEmail}
                      onChange={e => setNewEmail(e.target.value)}
                      style={{ width: '100%', padding: '6px 10px', borderRadius: 6, border: '1px solid #ccc' }}
                    />
                    
                  </td>
                  <td colSpan={isAdmin ? 3 : 2}>
                    <Button onClick={handleAddEmail}>Add Email</Button>
                    
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
                    {emailError && <div style={{color: 'red'}}>{emailError}</div>}

        </TableCard>

        <TableCard>
  <h3>Mail Trigger Settings</h3>
  {mailLoading && <Loader />}
  {mailError && <div style={{color: 'red'}}>{mailError}</div>}
  <Table>
    <thead>
      <tr>
        <th>Module Name</th>
        <th>Create Trigger</th>
        <th>Update Trigger</th>
        <th>Delete Trigger</th>
      </tr>
    </thead>
    <tbody>
      {mailTriggers.length === 0 ? (
        <tr>
          <td colSpan={4} style={{textAlign: 'center'}}>No mail triggers found</td>
        </tr>
      ) : (
        mailTriggers.map(({module_name, create_trigger, update_trigger, delete_trigger}) => (
          <tr key={module_name}>
            <td>{module_name}</td>
            {[['create_trigger', create_trigger], ['update_trigger', update_trigger], ['delete_trigger', delete_trigger]].map(([trigger, value]) => (
              <td key={trigger}>
                {isAdmin ? (
                  <ToggleButton
                    active={!!value}
                    onClick={() => toggleMailTrigger(module_name, trigger, value)}
                  >
                    {value ? 'Active' : 'Inactive'}
                  </ToggleButton>
                ) : value ? 'Active' : 'Inactive'}
              </td>
            ))}
          </tr>
        ))
      )}
    </tbody>
  </Table>
</TableCard>


        {/* Currency Denominations */}
        <TableCard>
          <h3>Currency Denominations</h3>
          {denomLoading && <Loader />}
          {/* {denomError && <div style={{color: 'red'}}>{denomError}</div>} */}
          <Table>
            <thead>
              <tr>
                <th>ID</th>
                <th>Denomination Value</th>
                <th>Is Active</th>
                {isAdmin && <th>Toggle Active</th>}
                {isAdmin && <th>Delete</th>}
              </tr>
            </thead>
            <tbody>
              {currencyDenoms.length === 0 ? (
                <tr><td colSpan={5} style={{textAlign: 'center'}}>No currency denominations found</td></tr>
              ) : currencyDenoms.map(({id, currency_denom_value, is_active}) => (
                <tr key={id}>
                  <td>{id}</td>
                  <td>{currency_denom_value}</td>
                  <td>{is_active ? 'Active' : 'Inactive'}</td>
                  {isAdmin && (
                    <td>
                      <ToggleButton
                        active={is_active}
                        onClick={() => toggleDenomActive(id, is_active)}
                      >
                        {is_active ? 'Deactivate' : 'Activate'}
                      </ToggleButton>
                    </td>
                  )}
                  {isAdmin && (
                    <td><ButtonCancel variant="cancel" onClick={() => DeleteDenom(id)}>Delete</ButtonCancel></td>
                  )}
                </tr>
              ))}
              {isAdmin && (
                <tr>
                  <td>New</td>
                  <td>
                    <input
                      type="text"
                      value={newDenomValue}
                      onChange={e => setNewDenomValue(e.target.value)}
                      style={{ width: '100%', padding: '6px 10px', borderRadius: 6, border: '1px solid #ccc' }}
                    />
                  </td>
                  <td colSpan={isAdmin ? 3 : 2}>
                    <Button onClick={handleAddDenom}>Add Denomination</Button>
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
                    {denomError && <div style={{color: 'red'}}>{denomError}</div>}

        </TableCard>

        {/* Digital Settlements */}
        <TableCard>
          <h3>Digital Settlements</h3>
          {digitalLoading && <Loader />}
          <Table>
            <thead>
              <tr>
                <th>ID</th>
                <th>Settlement Name</th>
                <th>Is Active</th>
                {isAdmin && <th>Toggle Active</th>}
                {isAdmin && <th>Delete</th>}
              </tr>
            </thead>
            <tbody>
              {digitalSettlements.length === 0 ? (
                <tr><td colSpan={5} style={{textAlign: 'center'}}>No digital settlements found</td></tr>
              ) : digitalSettlements.map(({id, digital_settlement_name, is_active}) => (
                <tr key={id}>
                  <td>{id}</td>
                  <td>{digital_settlement_name}</td>
                  <td>{is_active ? 'Active' : 'Inactive'}</td>
                  {isAdmin && (
                    <td>
                      <ToggleButton
                        active={is_active}
                        onClick={() => toggleDigitalActive(id, is_active)}
                      >
                        {is_active ? 'Deactivate' : 'Activate'}
                      </ToggleButton>
                    </td>
                  )}
                  {isAdmin && (
                    <td><ButtonCancel variant="cancel" onClick={() => DeleteDigitalSettlement(id)}>Delete</ButtonCancel></td>
                  )}
                </tr>
              ))}
              {isAdmin && (
                <tr>
                  <td>New</td>
                  <td>
                    <input
                      type="text"
                      placeholder="Digital Settlement Name"
                      value={newDigitalName}
                      onChange={e => setNewDigitalName(e.target.value)}
                      style={{ width: '100%', padding: '6px 10px', borderRadius: 6, border: '1px solid #ccc' }}
                    />
                  </td>
                  <td colSpan={isAdmin ? 3 : 2}>
                    <Button onClick={handleAddDigital}>Add Digital Settlement</Button>
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
                    {digitalError && <div style={{color: 'red'}}>{digitalError}</div>}

        </TableCard>
      </Container>
    </Page>
  );
}
